/**
 * The settings registry — every tunable value in the product, typed and versioned.
 *
 * Nothing user-facing is hard-coded, so this is where prices, windows, thresholds and limits
 * actually live. Three things the screen holds:
 *   - **Every value is typed**, and a paise value shows both the stored integer and the rupee
 *     rendering, so nobody has to guess which one the field wants.
 *   - **Every change is a revision** with a reason, and the previous value stays restorable.
 *   - **Some keys are safety-locked.** There is no setting that turns auditing off, or the
 *     outcome-claim lint, or four-eyes on write-offs — and a locked row says so rather than
 *     failing silently when someone tries.
 */
import { useEffect, useState } from 'react';
import { Button, Chip, Icon, StatusChip } from '../components/primitives';
import { Input } from '../components/forms';
import { DataTable, DetailPage } from '../components/data';
import { KV } from '../components/entities';
import { ConfirmAction, Consequences, DiffView, FilterBar, ScopeAction, Section } from '../components/kit';
import { formatInr } from '../lib/money';
import { QUEUE_HINT, useQueue } from '../lib/useQueue';
import { useConsole } from '../app/console';
import {
  isParked,
  useApproveSetting,
  useDeprecations,
  useRecentSettingRevisions,
  useRevertSetting,
  useSetting,
  useSettingApprovals,
  useSettings,
  useWriteSetting,
  type SettingRow,
} from '../lib/api/hooks/settings';
import { errorMessage } from '../lib/api/client';
import { isScope, type Scope } from '../lib/rbac';
import { useUrlSearch } from '../lib/filterCodecs';
import { formatDateTime } from '../lib/datetime';

/** A paise setting is stored as an integer; everything else renders as written. */
export function renderSettingValue(s: { type: string }, raw: string): string {
  return s.type === 'paise' ? formatInr(Number.parseInt(raw || '0', 10)) : raw;
}

const WHOLE = new Set(['int', 'integer', 'paise']);
const DECIMAL = new Set(['float', 'number']);
const FLAG = new Set(['bool', 'boolean']);

/**
 * Why the typed value would be refused, said before the confirm rather than after it. The server
 * stays the judge (422 `validation_failed`); this only stops the console offering to save "abc"
 * into an int with a dialog that reads "from 24 to abc". Bounds and choices come from the row.
 */
export function settingValueError(
  s: { type: string; minimum: number | null; maximum: number | null; choices: readonly unknown[] | null },
  raw: string,
): string | undefined {
  const v = raw.trim();
  if (WHOLE.has(s.type) && !/^-?\d+$/.test(v)) return 'A whole number';
  if (DECIMAL.has(s.type) && !/^-?\d+(\.\d+)?$/.test(v)) return 'A number';
  if (FLAG.has(s.type) && v !== 'true' && v !== 'false') return 'true or false';
  if (s.choices?.length && !s.choices.map(String).includes(v)) {
    return `One of: ${s.choices.map(String).join(', ')}`;
  }
  if (WHOLE.has(s.type) || DECIMAL.has(s.type)) {
    if (s.minimum !== null && Number(v) < s.minimum) return `At least ${s.minimum}`;
    if (s.maximum !== null && Number(v) > s.maximum) return `At most ${s.maximum}`;
  }
  return undefined;
}

/**
 * The registry row, mapped for rendering. Presentation only — every field comes from the feed,
 * because **the feed is the render model** (doc 27) and a UI-side catalogue of settings is a list
 * that silently goes stale and then hides a setting nobody can find.
 */
export interface SettingView {
  id: string;
  key: string;
  domain: string;
  label: string;
  note: string;
  type: string;
  value: string;
  source: string;
  editableBy: Scope;
  locked: boolean;
  fourEyes: boolean;
  unit: string | null;
  minimum: number | null;
  maximum: number | null;
  choices: unknown[] | null;
}

export function toSettingView(row: SettingRow): SettingView {
  return {
    id: row.key,
    key: row.key,
    domain: row.group,
    label: row.label,
    note: row.description,
    type: row.type,
    /* The registry types the value; the input edits text. `null` is a real stored value and must
       not become the string "null" in a field somebody then saves. */
    value: row.value === null || row.value === undefined ? '' : String(row.value),
    source: row.source,
    /* The registry declares which scope may edit each setting. An unrecognised one is treated as
       superadmin-only rather than as "anyone" — narrow, never widen (doc 02 §4.3). */
    editableBy: isScope(row.editable_by) ? row.editable_by : 'superadmin',
    locked: row.safety_locked,
    fourEyes: row.requires_second_approval,
    unit: row.unit ?? null,
    minimum: row.minimum ?? null,
    maximum: row.maximum ?? null,
    choices: row.choices ?? null,
  };
}

export function settingFilter(domain: string, s: { domain: string }): boolean {
  return domain === 'all' || s.domain === domain;
}

export function Settings() {
  const { scope, toast } = useConsole();
  const [q, setQ] = useUrlSearch('settings');
  const [domain, setDomain] = useState('all');
  const [confirm, setConfirm] = useState(false);
  const [value, setValue] = useState('');

  const registry = useSettings();
  const all = (registry.data?.items ?? []).map(toSettingView);
  /* The groups come from the feed too — a hard-coded tab list is the same stale-catalogue bug in
     smaller print. */
  const domains = [...new Set(all.map((s) => s.domain))];
  const rows = all.filter(
    (s) => settingFilter(domain, s) && `${s.key}${s.note}`.toLowerCase().includes(q.toLowerCase()),
  );
  const { focus, setFocus, open, setOpen } = useQueue(rows, { key: domain + q });

  const detail = useSetting(open?.key ?? null);
  const write = useWriteSetting();
  const revert = useRevertSetting();

  useEffect(() => {
    setValue(open ? open.value : '');
  }, [open]);

  const dirty = Boolean(open) && value !== open?.value;
  const valueError = open && dirty ? settingValueError(open, value) : undefined;
  const lastRevision = detail.data?.revisions[0];

  if (open) {
    return (
      <DetailPage
        title={open.key}
        code={open.type}
        // `source` is the registry's own word for where the value came from — `default`, `env` or
        // `override` — so the chip states it rather than inferring it from a comparison.
        status={
          open.source === 'override' ? (
            <StatusChip tone="info" label="Overridden" />
          ) : (
            <StatusChip label={open.source} />
          )
        }
        onClose={() => setOpen(null)}
        audit={
          lastRevision
            ? `Last changed ${formatDateTime(lastRevision.created_at)} by admin#${lastRevision.admin_user_id} — ${lastRevision.reason}`
            : 'Never changed in this environment'
        }
        actions={
          <>
            {open.source === 'override' && (
            <ScopeAction
              scope={scope}
              need={open.editableBy}
              size="sm"
              variant="secondary"
              disabled={open.locked || revert.isPending}
              onClick={() =>
                revert.mutate(
                  { key: open.key, reason: 'Reverted to the registry default' },
                  {
                    onSuccess: () => {
                      setOpen(null);
                      toast({ tone: 'success', message: 'Override removed · back to the default' });
                    },
                    onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
                  },
                )
              }
              >
                Remove override
              </ScopeAction>
            )}
            <ScopeAction
              scope={scope}
              need={open.editableBy}
              size="sm"
              disabled={!dirty || open.locked || valueError !== undefined}
              onClick={() => setConfirm(true)}
            >
              {open.fourEyes ? 'Request approval' : 'Save revision'}
            </ScopeAction>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink2)' }}>{open.note}</p>

        <Input
          density="admin"
          label={`Value (${open.type})`}
          value={value}
          onChange={setValue}
          prefix={open.type === 'paise' ? '₹' : undefined}
          helper={
            open.type === 'paise'
              ? `Stored as integer paise: ${value} → ${formatInr(Number.parseInt(value || '0', 10), { paise: true })}`
              : open.unit
                ? `Measured in ${open.unit}`
                : `Currently from ${open.source}`
          }
          disabled={open.locked}
          error={valueError}
        />

        <KV
          rows={[
            ['Type', open.type],
            ['Value comes from', open.source],
            ['Editable by', open.editableBy],
            ['Group', open.domain],
            ['Takes effect', open.fourEyes ? 'After a second admin approves' : 'Immediately on save'],
          ]}
        />

        {dirty && <DiffView labels={['Current', 'New']} rows={[[open.key, open.value, value]]} />}

        <Section title="Revision history" note="Newest first · every entry carries a reason" pad={false}>
          {detail.isPending ? (
            <div style={{ padding: 'var(--sp-lg)', color: 'var(--c-ink3)', fontSize: 'var(--fs-admin-cell)' }}>
              Loading revisions…
            </div>
          ) : detail.isError ? (
            /* Not "never changed". A history that failed to load and a history that is empty look
               the same on screen and mean opposite things, and on a page about who changed what,
               silently reporting "nobody" is the worse of the two to get wrong. */
            <div
              role="alert"
              style={{ padding: 'var(--sp-lg)', color: 'var(--c-error-text)', fontSize: 'var(--fs-admin-cell)' }}
            >
              {errorMessage(detail.error)}
            </div>
          ) : detail.data!.revisions.length === 0 ? (
            <div style={{ padding: 'var(--sp-lg)', color: 'var(--c-ink3)', fontSize: 'var(--fs-admin-cell)' }}>
              This setting has never been changed in this environment.
            </div>
          ) : (
            <div style={{ display: 'grid' }}>
              {detail.data!.revisions.map((r, i) => (
                <div
                  key={r.id}
                  style={{
                    padding: 'var(--sp-md) var(--sp-lg)',
                    borderTop: i ? '1px solid var(--c-border)' : 'none',
                    display: 'grid',
                    gap: 4,
                    fontSize: 'var(--fs-admin-cell)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)' }}>
                    <span className="mono-num" style={{ color: 'var(--c-ink3)', textDecoration: 'line-through' }}>
                      {JSON.stringify(r.old_value_json)}
                    </span>
                    <Icon name="arrow-right" size={12} style={{ color: 'var(--c-ink3)' }} />
                    <span className="mono-num" style={{ color: 'var(--c-saffron-text)' }}>
                      {JSON.stringify(r.new_value_json)}
                    </span>
                    <span style={{ flex: 1 }} />
                    <span style={{ color: 'var(--c-ink3)' }}>{formatDateTime(r.created_at)}</span>
                  </div>
                  <span style={{ color: 'var(--c-ink2)' }}>
                    admin#{r.admin_user_id} — {r.reason}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Section>

        {open.fourEyes && (
          <Consequences
            tone="warn"
            items={[
              'This key needs a second admin: saving parks the request in the shared four-eyes queue.',
              'You cannot approve your own request.',
            ]}
          />
        )}

        {open.locked && (
          <Consequences
            tone="error"
            items={[
              'Safety-locked. No setting exists that disables auditing, four-eyes on write-offs, or the outcome-claim lint.',
            ]}
          />
        )}

        <ConfirmAction
          open={confirm}
          title="Save this setting revision?"
          phrase="SAVE"
          tone="warn"
          reason
          consequences={[
            open.fourEyes
              ? `Parks a change to ${open.key} (${open.value} → ${value}) for a second approver — nothing changes until they approve.`
              : `Changes ${open.key} from ${open.value} to ${value} platform-wide, immediately.`,
            /* Said of this key, not of the platform: the old line told every change — an email
               link's lifetime included — that it touched every open booking. */
            `Read fresh by everything that uses ${open.key}; nothing already done is recomputed.`,
            'Creates a revision with your name and reason; the previous value stays restorable.',
          ]}
          onCancel={() => setConfirm(false)}
          onConfirm={(reason) => {
            setConfirm(false);
            write.mutate(
              { key: open.key, value, reason },
              {
                onSuccess: (outcome) => {
                  setOpen(null);
                  /* Parked and applied are different outcomes, and reporting "saved" for a change
                     that has not happened is how a four-eyes gate quietly stops being one. */
                  toast(
                    isParked(outcome)
                      ? { tone: 'info', message: `Parked for a second approver · request #${outcome.id}` }
                      : { tone: 'success', message: 'Revision saved · effective now' },
                  );
                },
                onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
              },
            );
          }}
          confirmLabel="Save revision"
        />
      </DetailPage>
    );
  }

  return (
    <>
      <ApprovalQueue />
      <DeprecatedSurfaces />
      <FilterBar
        q={q}
        onQ={setQ}
        placeholder="Setting key or description — press /"
        tab={domain}
        onTab={setDomain}
        tabs={[
          { key: 'all', label: `All · ${all.length}` },
          ...domains.map((d) => ({
            key: d,
            label: `${d} · ${all.filter((s) => s.domain === d).length}`,
          })),
        ]}
        right={
          <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
            Every value is typed · every change is a revision
          </span>
        }
      />

      <DataTable
        density="compact"
        focusedIndex={focus}
        onFocusRow={setFocus}
        onRowClick={(row) => setOpen(all.find((s) => s.id === row.id)!)}
        emptyLabel="No setting matches this filter"
        footer={
          <>
            <span>{rows.length} settings</span>
            <span>{QUEUE_HINT}</span>
          </>
        }
        columns={[
          { key: 'domain', label: 'Domain' },
          { key: 'key', label: 'Key', mono: true },
          { key: 'type', label: 'Type' },
          { key: 'value', label: 'Value', align: 'right', mono: true },
          { key: 'default', label: 'Default', align: 'right', mono: true },
          { key: 'state', label: 'State' },
          { key: 'editable', label: 'Editable by', mono: true },
        ]}
        rows={rows.map((s) => ({
          id: s.id,
          domain: s.domain,
          key: s.key,
          type: s.type,
          value: (
            <span style={{ color: s.source === 'override' ? 'var(--c-saffron-text)' : 'var(--c-ink)' }}>
              {renderSettingValue(s, s.value)}
            </span>
          ),
          default: <span style={{ color: 'var(--c-ink3)' }}>{s.source}</span>,
          state: (
            <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {s.source === 'override' ? <StatusChip tone="info" label="Overridden" /> : <StatusChip label={s.source} />}
              {s.fourEyes && <StatusChip tone="warn" label="Second approval" />}
              {s.locked && <StatusChip tone="neutral" label="Safety-locked" icon="lock" />}
            </span>
          ),
          editable: s.editableBy,
        }))}
      />

      <RecentRevisions />
    </>
  );
}

/**
 * The design's list-page "Revision history" strip: the three newest changes across every setting,
 * from `GET /v1/admin/settings/revisions` (doc 41 §9, "what changed?"). Per-key history stays on
 * the detail page.
 */
function RecentRevisions() {
  const recent = useRecentSettingRevisions(3);
  return (
    <Section title="Revision history" note="Newest first · every entry carries a reason" pad={false}>
      {recent.isPending ? (
        <div style={{ padding: 'var(--sp-lg)', color: 'var(--c-ink3)', fontSize: 'var(--fs-admin-cell)' }}>
          Loading revisions…
        </div>
      ) : recent.isError ? (
        <div role="alert" style={{ padding: 'var(--sp-lg)', color: 'var(--c-error-text)', fontSize: 'var(--fs-admin-cell)' }}>
          {errorMessage(recent.error)}
        </div>
      ) : recent.data.items.length === 0 ? (
        <div style={{ padding: 'var(--sp-lg)', color: 'var(--c-ink3)', fontSize: 'var(--fs-admin-cell)' }}>
          No setting has been changed in this environment yet.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
          {recent.data.items.map((r, i) => (
            <div
              key={r.id}
              style={{
                padding: 'var(--sp-md) var(--sp-lg)',
                borderLeft: i ? '1px solid var(--c-border)' : 'none',
                display: 'grid',
                gap: 4,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)', fontSize: 'var(--fs-admin-cell)' }}>
                <span className="mono-num" style={{ color: 'var(--c-ink)' }}>
                  {r.setting_key}
                </span>
                <span style={{ flex: 1 }} />
                <span style={{ color: 'var(--c-ink3)' }}>{formatDateTime(r.created_at)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)', fontSize: 'var(--fs-admin-cell)' }}>
                <span className="mono-num" style={{ color: 'var(--c-ink3)', textDecoration: 'line-through' }}>
                  {JSON.stringify(r.old_value_json)}
                </span>
                <Icon name="arrow-right" size={12} style={{ color: 'var(--c-ink3)' }} />
                <span className="mono-num" style={{ color: 'var(--c-saffron-text)' }}>
                  {JSON.stringify(r.new_value_json)}
                </span>
                <span style={{ flex: 1 }} />
                <span style={{ color: 'var(--c-ink3)' }}>admin#{r.admin_user_id}</span>
              </div>
              <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>{r.reason}</span>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

/**
 * The parked four-eyes changes — the half of the gate that used to be missing.
 *
 * A `PUT` to a four-eyes setting returns an approval, not a row: the change is parked, not applied.
 * Until `GET /v1/admin/settings/approvals` existed, the only place that `approval_id` ever appeared
 * was in the response to the requester's own write — and the requester is the one admin forbidden
 * from approving it. So the gate could be opened and never closed (CSR-022).
 *
 * It renders nothing when the queue is empty. A permanently visible "0 pending" panel above every
 * settings screen is a banner people stop reading, and this one needs to be noticed on the day it
 * is not empty.
 *
 * **Who may approve is the server's rule.** It refuses the requester's own approval with a 403, and
 * that refusal is shown verbatim rather than pre-empted — a client that decided who was allowed
 * would be a second copy of a control that only counts once.
 */
export function ApprovalQueue() {
  const { toast } = useConsole();
  const approvals = useSettingApprovals();
  const approve = useApproveSetting();

  const pending = approvals.data?.items ?? [];
  if (pending.length === 0) return null;

  return (
    <Section
      title={`${pending.length} change${pending.length === 1 ? '' : 's'} waiting for a second admin`}
      note="A four-eyes setting parks rather than applies. Nothing here has taken effect yet."
    >
      <div style={{ display: 'grid', gap: 'var(--sp-xs)' }}>
        {pending.map((a) => (
          <div
            key={a.id}
            style={{
              display: 'flex',
              gap: 'var(--sp-sm)',
              alignItems: 'center',
              padding: 'var(--sp-sm) var(--sp-md)',
              border: '1px solid var(--c-saffron)',
              background: 'var(--c-saffron-50)',
              borderRadius: 'var(--r-tile)',
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 500 }}>{a.setting_key}</div>
              <div style={{ color: 'var(--c-ink2)', fontSize: 'var(--fs-admin-colhead)' }}>
                → {String(a.proposed_value_json)} · {a.reason}
              </div>
            </div>
            <Chip label={`requested by admin ${a.requested_by_admin_id}`} />
            <Button
              size="sm"
              onClick={() =>
                approve.mutate(
                  { key: a.setting_key, approvalId: a.id },
                  {
                    onSuccess: () => toast({ tone: 'success', message: `${a.setting_key} applied` }),
                    onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
                  },
                )
              }
            >
              Approve
            </Button>
          </div>
        ))}
      </div>
    </Section>
  );
}

/**
 * API surfaces being retired, soonest sunset first (arch-16 §13). Hidden while there are none,
 * like the approval queue: an empty box on every visit would teach people to stop looking.
 * The dates change by PR, never here, so there is nothing to edit.
 */
export function DeprecatedSurfaces() {
  const surfaces = useDeprecations();
  const items = surfaces.data?.items ?? [];
  if (items.length === 0) return null;

  return (
    <Section
      title={`${items.length} API surface${items.length === 1 ? '' : 's'} on the deprecation ladder`}
      note="Each answers 410 after its sunset. Removing the code waits for 30 days of zero traffic."
    >
      <DataTable
        density="compact"
        columns={[
          { key: 'name', label: 'Surface', mono: true },
          { key: 'sunset', label: 'Sunset' },
          { key: 'replacement', label: 'Use instead', mono: true },
          { key: 'state', label: 'State' },
        ]}
        rows={items.map((d) => ({
          id: d.name,
          name: d.name,
          sunset: d.sunset,
          replacement: d.replacement,
          state: d.gone ? (
            <StatusChip tone="error" label="Gone · answers 410" />
          ) : (
            <StatusChip tone="warn" label="Deprecated" />
          ),
        }))}
      />
    </Section>
  );
}
